'use client'

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Text } from '@chakra-ui/react'
import { Location } from '@leid/api-locations'
import { startOfMonth } from 'date-fns'
import sortByDistance from 'sort-by-distance'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate } from 'app/utils/dates'
import { getDistanceCoordinates } from 'app/utils/distance'
import { ItemApi } from 'appTypes/items'
import { SortingInfo } from 'appTypes/mapper'
import { CartFormValues } from 'appTypes/orders'
import { ButtonText } from 'components/ui/Buttons/Button'
import apiClient from 'helpers/clients/apiClient'
import { isProductAvailable } from 'helpers/utils/availabilities'

import RelatedProductCard from './RelatedProductCard'

type LocationWithDistance = Location & {
  distance: number
}

type Props = {
  product: ItemApi
  products: ItemApi[]
  values: CartFormValues
  isRerent: boolean
  customerInfo: CustomerInfo
  sortingInfo: SortingInfo | undefined
}

const RelatedProducts = (props: Props): JSX.Element | null => {
  const { product, products, values, isRerent, customerInfo, sortingInfo } = props

  const [areAllShown, setAreAllShown] = useState(false)
  const [allLocations, setAllLocations] = useState<Location[] | undefined>(undefined)
  const [relatedItems, setRelatedItems] = useState<{ product: ItemApi; location: Location | undefined }[]>([])

  const { t } = useTranslation('common')

  useEffect(() => {
    if (window.innerWidth >= 1023) {
      setAreAllShown(true)
    }
  }, [])

  useEffect(() => {
    getProductsToShow()
  }, [values, product.id])

  const getAllLocations = async () => {
    const response = await apiClient.getRealLocations({ active: true })
    const allLocations = response.data?.rows ?? []
    setAllLocations(allLocations)
    return allLocations
  }

  const getCloserLocations = async () => {
    const locations = allLocations ?? (await getAllLocations())
    const selectedLocation = locations.find((s) => s.id === values.selectedLocationId)
    if (!selectedLocation) {
      return undefined
    }

    const distanceCoordinates = getDistanceCoordinates(sortingInfo, customerInfo)
    if (!distanceCoordinates) {
      return locations
    }

    const locationsWithDistance = sortByDistance(
      { long: distanceCoordinates?.longitudeCoordinate, lat: distanceCoordinates?.latitudeCoordinate },
      locations,
      { yName: 'lat', xName: 'long' },
    ) as LocationWithDistance[]

    const index = locationsWithDistance.findIndex((item) => item.id === selectedLocation.id)
    const closerLocations = locationsWithDistance
      .slice(0, index)
      .filter((s) => s.distance < locationsWithDistance[index].distance)
    return closerLocations
  }

  const getProductsToShow = async () => {
    const productAvailable = isAvailable()
    if (!productAvailable) {
      setRelatedItems([])
      return
    }

    const closerLocations = await getCloserLocations()
    if (!closerLocations) {
      // No selected location, show everything
      setRelatedItems([])
      return
    }

    if (closerLocations.length === 0) {
      // There's no closer locations - do not show anything
      setRelatedItems([])
      return
    }

    const result: { product: ItemApi; location: Location | undefined }[] = []

    for (const product of products) {
      const calendarResponse = await apiClient.getProductCalendarList(product.id, {
        startDate: startOfMonth(new Date()),
      })
      if (!calendarResponse.success || !calendarResponse.data) {
        continue
      }
      const openingHoursResponse = await apiClient.getProductOpeningHours(product.id)
      if (!openingHoursResponse.success || !openingHoursResponse.data) {
        continue
      }

      const availabilities = calendarResponse.data ?? []
      const openingHours = openingHoursResponse.data ?? []
      const rentedFrom = buildDate(values.startDate, values.pickupAt)
      const rentedTo = buildDate(values.endDate, values.returnAt)

      for (const location of closerLocations) {
        const isAvailable = isProductAvailable(
          location.id,
          rentedFrom,
          rentedTo,
          undefined,
          availabilities,
          openingHours,
          true,
          true,
          1,
        )
        if (isAvailable) {
          result.push({ product, location })
          break
        }
      }
    }

    setRelatedItems(result)
  }

  const isAvailable = () => {
    if (isRerent) {
      return false
    }

    if (
      !values.returnAt ||
      !values.endDate ||
      !values.selectedLocationId ||
      !values.selectedRegionId ||
      !values.startDate ||
      !values.pickupAt
    ) {
      return false
    }

    return true
  }

  if (relatedItems.length === 0) {
    return null
  }

  return (
    <Box width="100%" marginBottom="30px">
      <Flex marginTop="36px" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm">{t('relatedProducts')}</Text>
        {products.length > 1 && (
          <Box display={{ lg: 'none' }}>
            <ButtonText onClick={() => setAreAllShown(!areAllShown)} href={undefined}>
              {t(areAllShown ? 'showLess' : 'showAll')}
            </ButtonText>
          </Box>
        )}
      </Flex>
      <Box
        marginTop="12px"
        display="grid"
        gridGap="20px"
        gridTemplateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
      >
        {relatedItems?.slice(0, 2).map((info) => (
          <RelatedProductCard
            key={info.product.id}
            product={info.product}
            values={values}
            closerLocation={info.location}
          />
        ))}

        {areAllShown && (
          <>
            {relatedItems?.slice(2, products.length).map((info) => (
              <RelatedProductCard
                key={info.product.id}
                product={info.product}
                values={values}
                closerLocation={info.location}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}

export default RelatedProducts
